import React, { useState, useRef } from 'react';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { EnquiryForm as EnquiryFormType } from '../types';
import { submitEnquiry } from '../utils/api';
import { WHATSAPP_URL, WHATSAPP_DISPLAY } from '../utils/constants';
import SEOHead from '../components/SEOHead';
import './Contact.css';

type Status = 'idle' | 'loading' | 'success' | 'error' | 'captcha';
const EMPTY: EnquiryFormType = { name: '', phone: '', email: '', message: '' };
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '';
const EMAIL_RE = /^\S+@\S+\.\S+$/;
const PHONE_RE = /^\+?[0-9][0-9\s-]{7,18}$/;

const Contact: React.FC = () => {
  const [form,           setForm]           = useState<EnquiryFormType>(EMPTY);
  const [status,         setStatus]         = useState<Status>('idle');
  const [errorMessage,   setErrorMessage]   = useState<string>('');
  const [honeypot,       setHoneypot]       = useState<string>('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const openedAt = useRef(Date.now()); // when this form first became visible

  const set = (k: keyof EnquiryFormType, v: string): void => {
    setForm(f => ({ ...f, [k]: v }));
    if (status === 'error') setStatus('idle');
    setErrorMessage('');
  };

  const submit = async (): Promise<void> => {
    const name = form.name.trim();
    const phone = form.phone.trim();
    const email = form.email.trim();
    if (name.length < 2 || name.length > 100) {
      setErrorMessage('Please enter a name between 2 and 100 characters.'); setStatus('error'); return;
    }
    if (!PHONE_RE.test(phone)) {
      setErrorMessage('Please enter a valid WhatsApp or phone number.'); setStatus('error'); return;
    }
    if (email.length > 254 || !EMAIL_RE.test(email)) {
      setErrorMessage('Please enter a valid email address.'); setStatus('error'); return;
    }
    if (form.message.length > 2000) {
      setErrorMessage('Your message must be 2,000 characters or fewer.'); setStatus('error'); return;
    }
    if (!recaptchaToken) { setStatus('captcha'); return; }
    setStatus('loading');
    try {
      await submitEnquiry(form, 'General Enquiry', undefined, recaptchaToken, honeypot, openedAt.current);
      setStatus('success');
      setForm(EMPTY);
      setRecaptchaToken(null);
      recaptchaRef.current?.reset();
    } catch (error) {
      const apiError = axios.isAxiosError<{ error?: string }>(error)
        ? error.response?.data?.error
        : undefined;
      setErrorMessage(apiError || 'Unable to send your enquiry. Please try again later.');
      setStatus('error');
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }
  };

  return (
    <>
    <SEOHead
      title="Contact Us"
      description={`Contact Balaji Fashion House for enquiries about Indian ethnic wear. WhatsApp us at ${WHATSAPP_DISPLAY} or send an email.`}
      keywords="contact Balaji Fashion House, WhatsApp fashion enquiry, ethnic wear contact"
      url="/contact"
      localBusiness
    />
    <div className="contact-page">
      <div className="contact-hero">
        <div className="container">
          <h1>Get in Touch</h1>
          <p>We'd love to hear from you. Chat with us instantly on WhatsApp!</p>
        </div>
      </div>

      <div className="container contact-layout">
        <div className="contact-info">
          <h2>Contact Us</h2>
          <p className="contact-tagline">
            We're here to help with product enquiries, bulk orders, and anything else you need.
          </p>
          <div className="contact-cards">
            <a href={WHATSAPP_URL('Hi! I have an enquiry.')} target="_blank" rel="noreferrer"
               className="contact-card whatsapp-card">
              <div className="cc-icon">💬</div>
              <div>
                <div className="cc-title">WhatsApp (Fastest)</div>
                <div className="cc-value">{WHATSAPP_DISPLAY}</div>
                <div className="cc-sub">Usually replies within minutes</div>
              </div>
            </a>
            {[
              { icon: '📧', title: 'Email',         value: 'balajifashioncollectionhouse@gmail.com', sub: 'Reply within 24 hours' },
              { icon: '🛍️', title: 'Amazon Store',  value: 'Balaji Fashion House',        sub: 'View on Amazon India' },
              { icon: '🕐', title: 'Business Hours', value: 'Mon–Sat: 10am–7pm',          sub: 'Sunday: 11am–4pm' },
            ].map(c => (
              <div key={c.title} className="contact-card">
                <div className="cc-icon">{c.icon}</div>
                <div>
                  <div className="cc-title">{c.title}</div>
                  <div className="cc-value">{c.value}</div>
                  <div className="cc-sub">{c.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="contact-form-wrap">
          <h2>Send an Enquiry</h2>
          {status === 'success' ? (
            <div className="form-success">
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
              <h3>Message Sent!</h3>
              <p>Thank you! We've sent a confirmation to your email and will reply within 24 hours.</p>
              <button className="btn-primary" onClick={() => setStatus('idle')} style={{ marginTop: '16px' }}>
                Send Another
              </button>
            </div>
          ) : (
            <>
              {/* Honeypot — hidden from humans, bots fill it */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={e => setHoneypot(e.target.value)}
                style={{ display: 'none' }}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              <div className="contact-form-grid">
                {([
                  ['name',    'Full Name *',          'Your name',         'text' ],
                  ['phone',   'WhatsApp / Phone *',   '+91 XXXXX XXXXX',   'tel'  ],
                  ['email',   'Email *',              'your@email.com',    'email'],
                ] as [keyof EnquiryFormType, string, string, string][]).map(([key, label, ph, type]) => (
                  <div key={key} className="field">
                    <label htmlFor={`cf-${key}`}>{label}</label>
                    <input id={`cf-${key}`} type={type} value={form[key]}
                           onChange={e => set(key, e.target.value)} placeholder={ph} required
                           maxLength={key === 'name' ? 100 : key === 'phone' ? 20 : 254}
                           inputMode={key === 'phone' ? 'tel' : key === 'email' ? 'email' : 'text'}
                           autoComplete={key === 'name' ? 'name' : key === 'phone' ? 'tel' : 'email'} />
                  </div>
                ))}
                <div className="field cf-full">
                  <label htmlFor="cf-message">Message</label>
                  <textarea id="cf-message" value={form.message} onChange={e => set('message', e.target.value)}
                            placeholder="Tell us about your enquiry — product details, bulk order, custom request..."
                            rows={5} maxLength={2000} />
                </div>
              </div>

              <div className="contact-captcha-wrap">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={SITE_KEY}
                  onChange={token => setRecaptchaToken(token)}
                  onExpired={() => setRecaptchaToken(null)}
                />
              </div>

              {status === 'error' && (
                <p className="form-error" role="alert">{errorMessage || 'Please check the form and try again.'}</p>
              )}
              {status === 'captcha' && (
                <p className="form-error" role="alert">Please complete the CAPTCHA verification.</p>
              )}
              <button className="btn-primary btn-lg btn-block cf-submit" onClick={submit} disabled={status === 'loading'}>
                {status === 'loading' ? 'Sending…' : 'Send Enquiry'}
              </button>
              <a href={WHATSAPP_URL()} target="_blank" rel="noreferrer" className="btn-whatsapp btn-block">
                💬 Or Chat Directly on WhatsApp
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  </>
  );
};

export default Contact;
